import type { ReactNode } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { UserRole } from '@manpower/shared';
import { useAuth } from './AuthProvider';

interface RequireCompanyProps {
  /** Wraps a single element; omit to gate a whole nested branch via <Outlet />. */
  children?: ReactNode;
}

/**
 * Sits inside `ProtectedRoute` for the employer branch, guarding every
 * employer route except `/employer/company/setup` itself.
 *
 * A Google registration leaves an employer with no linked company — Google
 * supplies no company name, so there is nothing to create it from at sign-up.
 * Every other employer endpoint 403s on that state, so this sends them to the
 * one screen that can fix it instead of a dashboard rendering the API's
 * rejection as a generic error.
 *
 * `ProtectedRoute` has already resolved the session (and redirected an
 * anonymous visitor to /login) by the time this renders, so there is no
 * `isLoading` or `!user` check here — duplicating it would show a second
 * spinner for no reason.
 */
export function RequireCompany({ children }: RequireCompanyProps) {
  const { user } = useAuth();

  if (user?.role === UserRole.EMPLOYER && !user.companyId) {
    return <Navigate to="/employer/company/setup" replace />;
  }

  return <>{children ?? <Outlet />}</>;
}
